/**
 * Universal Visual Builder — platform-aware component registry.
 *
 * Phase 2 additions:
 *   • Per-component `supports` metadata (image/icon/content/width/align)
 *     so the RIGHT settings panel can show only the safe controls that
 *     make sense for the selected component.
 *   • `GLOBAL_STYLE_SCHEMA` — colours, typography, radii and spacing
 *     tokens edited from the dedicated Global Style pane.
 *
 * Components declared here are intentionally UI-only. Business-logic
 * fields are never exposed (no discount formulas, no tax rules, no
 * stock deduction, no auth).
 */

export const PLATFORMS = [
  {
    id: "customer_app",
    label: "Customer App",
    hint: "Native customer-facing mobile app",
    live: false,          // consumer wired in Phase 5
  },
  {
    id: "website",
    label: "Website",
    hint: "babubaazar.in customer website",
    live: true,           // consumer already reads /ui-customization
  },
  {
    id: "store_admin",
    label: "Store Admin",
    hint: "Store Admin app (dashboard, orders, inventory)",
    live: false,          // consumer wired in Phase 4
  },
  {
    id: "pos",
    label: "POS",
    hint: "In-store point of sale billing screen",
    live: false,          // consumer wired in Phase 3
  },
];

// Groups shown in the LEFT palette.
const GROUP = {
  SECTIONS: "Sections",
  CARDS:    "Cards / Widgets",
  ACTIONS:  "Actions",
  GENERAL:  "General",
};

// Default `supports` shape — every component supports the common
// presentation settings. Additional flags are opt-in per component.
const DEFAULT_SUPPORTS = {
  image: false,
  icon: false,
  content: false,     // label / text override
  width: true,        // full / half / third / auto
  align: true,        // left / center / right
  border: true,
  margin: true,
};

const c = (id, label, group, extras = {}) => ({
  id, label, group,
  icon: extras.icon || null,
  supports: { ...DEFAULT_SUPPORTS, ...(extras.supports || {}) },
  // Phase 7/8 — richer editor hook (Visual Builder shows a custom editor
  // panel for these components instead of the generic settings pane).
  editor: extras.editor || null, // "product_card" | "category_layout" | null
});

// Common building blocks reused across every platform.
const COMMON_GENERAL = [
  c("text",        "Text",        GROUP.GENERAL, { supports: { content: true, image: false } }),
  c("image",       "Image",       GROUP.GENERAL, { supports: { image: true, content: false } }),
  c("button",      "Button",      GROUP.ACTIONS, { supports: { content: true, icon: true } }),
  c("icon_button", "Icon Button", GROUP.ACTIONS, { supports: { icon: true } }),
  c("divider",     "Divider",     GROUP.SECTIONS, { supports: { content: false, align: false } }),
  c("spacer",      "Spacer",      GROUP.SECTIONS, { supports: { content: false, border: false } }),
];

export const REGISTRY = {
  website: {
    components: [
      c("hero",              "Hero Banner",       GROUP.SECTIONS, { supports: { image: true, content: true } }),
      c("homepage_sections", "Homepage Sections", GROUP.SECTIONS, { supports: { content: false, image: false, icon: false }, editor: "homepage_sections" }),
      c("categories",        "Category Grid",     GROUP.SECTIONS, { supports: { content: true }, editor: "category_layout" }),
      c("product_row",       "Product Row",       GROUP.SECTIONS, { supports: { content: true }, editor: "category_layout" }),
      c("product_grid",      "Product Grid",      GROUP.SECTIONS, { supports: { content: true }, editor: "category_layout" }),
      c("product_card",      "Product Card",      GROUP.CARDS,    { supports: { content: false, image: false, icon: false }, editor: "product_card" }),
      c("deal_banner",       "Deal Banner",       GROUP.CARDS,    { supports: { image: true, content: true } }),
      c("footer",            "Footer",            GROUP.SECTIONS, { supports: { content: true } }),
      ...COMMON_GENERAL,
    ],
  },

  customer_app: {
    components: [
      c("app_header",        "App Header",        GROUP.SECTIONS, { supports: { content: true, icon: true } }),
      c("search",            "Search Bar",        GROUP.SECTIONS, { supports: { content: true, icon: true } }),
      c("location_pill",     "Location Pill",     GROUP.SECTIONS, { supports: { content: true, icon: true } }),
      c("hero",              "Hero Banner",       GROUP.SECTIONS, { supports: { image: true, content: true } }),
      c("homepage_sections", "Homepage Sections", GROUP.SECTIONS, { supports: { content: false, image: false, icon: false }, editor: "homepage_sections" }),
      c("category_slider",   "Category Slider",   GROUP.SECTIONS, { supports: { content: true }, editor: "category_layout" }),
      c("product_grid",      "Product Grid",      GROUP.SECTIONS, { supports: { content: true }, editor: "category_layout" }),
      c("product_card",      "Product Card",      GROUP.CARDS,    { supports: { content: false, image: false, icon: false }, editor: "product_card" }),
      c("cart_pill",         "Cart Pill",         GROUP.CARDS,    { supports: { content: true, icon: true } }),
      ...COMMON_GENERAL,
    ],
  },

  store_admin: {
    components: [
      c("dash_header",    "Dashboard Header",GROUP.SECTIONS, { supports: { content: true } }),
      c("sales_card",     "Sales Card",      GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("cash_card",      "Cash Card",       GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("upi_card",       "UPI Card",        GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("orders_card",    "Orders Card",     GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("profit_card",    "Profit Card",     GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("low_stock_card", "Low Stock Card",  GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("udhaar_card",    "Udhaar Card",     GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("quick_actions",  "Quick Actions",   GROUP.SECTIONS, { supports: { content: true } }),
      c("notification",   "Notification",    GROUP.SECTIONS, { supports: { content: true, icon: true } }),
      ...COMMON_GENERAL,
    ],
  },

  pos: {
    components: [
      // 🟢 WIRED regions — each ID is consumed by a real `<VBRegion>` in
      // apps/store-admin/app/(main)/pos.tsx AND by `<VBRegionWeb>` in the
      // web mirror at frontend/src/pages/AdminPOS.jsx (where applicable).
      // Editing here actually updates the running POS.
      c("pos_header",         "Header",           GROUP.SECTIONS, { supports: { content: true } }),
      c("pos_search",         "Search Bar",       GROUP.SECTIONS, { supports: { content: true, icon: true } }),
      c("pos_product_area",   "Product Area",     GROUP.SECTIONS, { supports: { content: true } }),
      c("pos_bill_table",     "Billing Table",    GROUP.CARDS,    { supports: { content: true } }),
      c("pos_totals_row",     "Totals · Discount · Add Product", GROUP.CARDS, { supports: { content: true } }),
      c("pos_charges",        "Charges Strip",    GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("pos_mini_cart",      "Mini Cart",        GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("pos_billing_bottom", "Billing Bottom",   GROUP.CARDS,    { supports: { content: true, icon: true } }),
      c("pos_payment_sheet",  "Payment Sheet",    GROUP.SECTIONS, { supports: { content: true } }),
      c("pos_card",           "Product Result",   GROUP.CARDS,    { supports: { content: false, image: false, icon: false }, editor: "pos_card" }),
      // Common building blocks (text/image/button/etc.) — reusable.
      ...COMMON_GENERAL,
    ],
  },
};

export function componentsFor(platformId) {
  return REGISTRY[platformId]?.components || [];
}

export function groupsFor(platformId) {
  const comps = componentsFor(platformId);
  const map = new Map();
  comps.forEach((cc) => {
    if (!map.has(cc.group)) map.set(cc.group, []);
    map.get(cc.group).push(cc);
  });
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}

export function componentById(platformId, compId) {
  return componentsFor(platformId).find((cc) => cc.id === compId) || null;
}

/**
 * Global Style tokens — safe, presentational only. Consumers (Phase 3–5)
 * read these and map to their own theme variables / native tokens.
 */
export const GLOBAL_STYLE_SCHEMA = {
  colors: [
    { key: "primary",    label: "Primary",       default: "#E32636" },
    { key: "secondary",  label: "Secondary",     default: "#111827" },
    { key: "bg",         label: "Background",    default: "#FFFFFF" },
    { key: "card_bg",    label: "Card Background", default: "#FFFFFF" },
    { key: "text",       label: "Text",          default: "#0F172A" },
    { key: "muted",      label: "Muted Text",    default: "#64748B" },
    { key: "success",    label: "Success",       default: "#16A34A" },
    { key: "warning",    label: "Warning",       default: "#F59E0B" },
    { key: "error",      label: "Error",         default: "#DC2626" },
  ],
  typography: [
    { key: "font_family",  label: "Font Family",  default: "Inter, system-ui, sans-serif" },
    { key: "heading_font", label: "Heading Font", default: "'DM Serif Display', serif" },
    { key: "body_size",    label: "Body Size (px)", default: 14, type: "number", min: 10, max: 20 },
    { key: "heading_weight", label: "Heading Weight", default: 700, type: "number", min: 400, max: 900, step: 100 },
  ],
  radii: [
    { key: "card_radius",   label: "Card Radius (px)",   default: 16, type: "number", min: 0, max: 48 },
    { key: "button_radius", label: "Button Radius (px)", default: 10, type: "number", min: 0, max: 48 },
    { key: "input_radius",  label: "Input Radius (px)",  default: 10, type: "number", min: 0, max: 48 },
  ],
  spacing: [
    { key: "spacing_sm", label: "Small (px)",  default: 8,  type: "number", min: 0, max: 48 },
    { key: "spacing_md", label: "Medium (px)", default: 16, type: "number", min: 0, max: 64 },
    { key: "spacing_lg", label: "Large (px)",  default: 24, type: "number", min: 0, max: 96 },
  ],
};

/** Flatten Global Style defaults into a single object. */
export function globalStyleDefaults() {
  const out = {};
  Object.values(GLOBAL_STYLE_SCHEMA).forEach((rows) =>
    rows.forEach((r) => { out[r.key] = r.default; })
  );
  return out;
}
